import React, { useEffect, lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

// === ABOVE-FOLD: Eager imports (critical for LCP) ===
import HeroSectionResponsive from "@/marketing/components/full-site/HeroSectionResponsive";

// === BELOW-FOLD: Lazy imports (code-split for smaller initial bundle) ===
// These components load on-demand, reducing main bundle from ~1.2MB to ~400KB
const HeroSectionAIChat = lazy(() => import("@/marketing/components/full-site/HeroSectionAIChat"));
const WaitlistSection = lazy(() => import("@/marketing/components/full-site/WaitlistSection"));
const StorySection = lazy(() => import("@/marketing/components/full-site/StorySection"));
const NaturalLanguageTradingSection = lazy(() => import("@/marketing/components/full-site/NaturalLanguageTradingSection"));
const FeaturesSection = lazy(() => import("@/marketing/components/full-site/FeaturesSection"));
const InstitutionalTeaserSection = lazy(() => import("@/marketing/components/full-site/InstitutionalTeaserSection"));
const SuccessHighlightsSection = lazy(() => import("@/marketing/components/full-site/SuccessHighlightsSection"));
const CapabilitiesStripSection = lazy(() => import("@/marketing/components/full-site/CapabilitiesStripSection"));
const GlobalMarketCoverageSection = lazy(() => import("@/marketing/components/full-site/ForexSection"));
const BlogTeaserSection = lazy(() => import("@/marketing/components/full-site/BlogTeaserSection"));
const PricingPreviewSection = lazy(() => import("@/marketing/components/full-site/PricingPreviewSection"));
const FAQTeaserSection = lazy(() => import("@/marketing/components/full-site/FAQTeaserSection"));
const ContactTeaserSection = lazy(() => import("@/marketing/components/full-site/ContactTeaserSection"));
const Footer = lazy(() => import("@/marketing/components/full-site/Footer"));
const ExplainerVideoSection = lazy(() => import("@/marketing/components/full-site/ExplainerVideoSection"));

// Minimal loading placeholder for lazy sections (matches site background)
const SectionFallback = () => (
  <div style={{ minHeight: '200px', background: '#020a0a' }} />
);

const Index = () => {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) {
      return;
    }

    const sectionId = location.hash.replace("#", "");
    const scrollToTarget = () => {
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = 96;
        const top =
          element.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: "smooth" });
      }
    };

    const timer = window.setTimeout(scrollToTarget, 120);

    return () => window.clearTimeout(timer);
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-gray-800/30 text-foreground relative">
      <div className="relative z-10">
        <Helmet>
          <title>MiDash - AI Investment Platform | Natural Language Trading</title>
          <meta name="description" content="Trade through simple conversations. MiDash's market-aware AI adapts to your style and market conditions. Natural language scanner, algo strategies, backtesting - trading made simple." />
          <meta name="keywords" content="conversational trading, natural language trading platform, AI trading, market scanner, algo trading, automated backtesting, risk management, MiDash" />
          
          <link rel="alternate" hrefLang="en" href="https://www.midash.ai/" />
          <link rel="alternate" hrefLang="ar" href="https://www.midash.ai/" />

          <meta property="og:title" content="MiDash - AI Investment Platform | Natural Language Trading" />
          <meta property="og:description" content="Trade through simple conversations. No complex terminals or jargon. MiDash's AI adapts to your trading style with natural language scanning, strategies, and 1-click broker integration." />
          
          <meta name="twitter:title" content="MiDash - AI Investment Platform | Natural Language Trading | منصة الاستثمار بالذكاء الاصطناعي" />
          <meta name="twitter:description" content="The future of trading is here. No complex terminals, just conversations. Natural language scanner, algo strategies, risk management - all through simple chat." />
          
          <script type="application/ld+json">
            {`
              {
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "MiDash AI-Powered Trading Platform",
                "description": "The future of trading through conversations. MiDash's market-aware AI understands your trading style. Natural language scanner, algo strategies, backtesting, and 1-click broker integration - no complex terminals needed.",
                "applicationCategory": "TradingPlatform",
                "operatingSystem": "Web, iOS, Android",
                "offers": {
                  "@type": "Offer",
                  "price": "79",
                  "priceCurrency": "USD"
                },
                "featureList": [
                  "Position Sizing Calculator",
                  "Risk Analytics Dashboard",
                  "AI Trading Assistant",
                  "Multi-Timeframe Analysis",
                  "Tick-Level Backtesting",
                  "Select Broker Integrations",
                  "Advanced Chart Types",
                  "Direct Exchange Data Feeds",
                  "Advanced Order Types",
                  "Portfolio Heat Maps"
                ],
                "audience": {
                  "@type": "Audience",
                  "audienceType": "Professional Traders, Serious Retail Traders, Fund Managers, Institutional Traders"
                },
                "keywords": "conversational trading, natural language scanner, algo trading platform, AI backtesting, risk management, MiDash"
              }
            `}
          </script>
          
          <script type="application/ld+json">
            {`
              {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://www.midash.ai/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Conversation Workflow",
                    "item": "https://www.midash.ai/#conversation-workflow"
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": "Pricing",
                    "item": "https://www.midash.ai/#pricing"
                  }
                ]
              }
            `}
          </script>
        </Helmet>
        {/* Navbar is rendered inside HeroSectionImmersive - it switches to CTA bar when active */}
        <main id="main-content">
          {/* ABOVE-FOLD: Hero loads eagerly for fast LCP */}
          <HeroSectionResponsive />

          {/* BELOW-FOLD: All sections lazy-loaded with Suspense */}
          {/* Wrapper div creates stacking context above hero's fixed background layer */}
          {/* z-index: 50 ensures content appears above all hero-bg-layer elements (z-index: 0-10) */}
          <Suspense fallback={<SectionFallback />}>
            <div style={{ position: 'relative', zIndex: 50, background: '#020a0a', isolation: 'isolate' }}>
              {/* SEO content - visually hidden but readable by crawlers */}
              {/* Inline styles ensure hiding even before Tailwind CSS loads */}
              <div className="sr-only" aria-hidden="true" style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>
                <HeroSectionAIChat />
              </div>
              <ExplainerVideoSection />
              {/* WaitlistSection removed — registration is closed, and its
                  only CTA was a "Register for Free" button pointing at the
                  now-retired /auth/register route. */}
              <StorySection />
              <NaturalLanguageTradingSection />
              <FeaturesSection />
              <InstitutionalTeaserSection />
              <SuccessHighlightsSection />
              <CapabilitiesStripSection />
              <GlobalMarketCoverageSection />
              <BlogTeaserSection />
              {/* PricingPreviewSection removed — a pricing table with no
                  reachable signup path is a dead end while capacity is full. */}
              <FAQTeaserSection />
              <ContactTeaserSection />
            </div>
          </Suspense>
        </main>
        <Suspense fallback={<SectionFallback />}>
          <div style={{ position: 'relative', zIndex: 50, background: '#020a0a', isolation: 'isolate' }}>
            <Footer />
          </div>
        </Suspense>
      </div>

    </div>
  );
};

export default Index;
